import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuthStore } from '@/stores/authStore';
import {
  isPendingOfferStatus,
  useWorkOrderOffers,
  type WorkOrderOffer,
  type WorkOrderOfferStatus,
} from '@/hooks/useWorkOrderOffers';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { EmailDeliveryHistory } from '@/components/email/EmailDeliveryHistory';
import { formatCentsForDisplay } from '@/utils/money';
import {
  eligibleOfferItems,
  hasPendingOffer,
  isOfferDeliverChannel,
  lineGrossCents,
  offerMoneyBuckets,
  sortOffersNewestFirst,
} from './offerDisplay';
import { CreateOfferDialog } from './CreateOfferDialog';
import { SendOfferDialog } from './SendOfferDialog';
import { RecordOfferDecisionDialog } from './RecordOfferDecisionDialog';
import { CancelOfferDialog } from './CancelOfferDialog';

const STATUS_COLOR: Record<WorkOrderOfferStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  sent: 'info',
  partially_decided: 'warning',
  approved: 'success',
  declined: 'error',
  expired: 'default',
  cancelled: 'default',
  superseded: 'default',
};

const LINE_DECISION_COLOR: Record<string, 'default' | 'success' | 'error'> = {
  pending: 'default',
  approved: 'success',
  declined: 'error',
};

interface WorkOrderOffersCardProps {
  workOrder: WorkOrderDetail;
}

export function WorkOrderOffersCard({ workOrder }: WorkOrderOffersCardProps) {
  const { t, i18n } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canUpdate = hasPermission('work_orders.update');
  const offersQuery = useWorkOrderOffers(workOrder.id);
  const offers = offersQuery.data?.offers ?? [];
  const sorted = useMemo(() => sortOffersNewestFirst(offers), [offers]);
  const buckets = useMemo(() => offerMoneyBuckets(offers), [offers]);
  const pendingExists = hasPendingOffer(offers);
  const createItems = useMemo(
    () => eligibleOfferItems(workOrder.items, offers),
    [workOrder.items, offers],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<WorkOrderOffer | null>(null);
  const [sendIsResend, setSendIsResend] = useState(false);
  const [decisionTarget, setDecisionTarget] = useState<WorkOrderOffer | null>(null);
  const [cancelTarget, setCancelTarget] = useState<WorkOrderOffer | null>(null);

  const formatDate = (iso: string) => new Date(iso).toLocaleString(i18n.language);

  return (
    <Box data-testid="work-order-offers-card" sx={{ mt: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6">{t('offers.cardTitle')}</Typography>
        {canUpdate && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            {t('offers.newOffer')}
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`${t('offers.bucketAgreed')}: ${formatCentsForDisplay(buckets.agreed)}`}
        />
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`${t('offers.bucketAwaiting')}: ${formatCentsForDisplay(buckets.awaiting)}`}
        />
        <Chip
          size="small"
          color="error"
          variant="outlined"
          label={`${t('offers.bucketDeclined')}: ${formatCentsForDisplay(buckets.declined)}`}
        />
      </Stack>

      {offersQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {offersQuery.isError && <Alert severity="error">{t('offers.loadFailed')}</Alert>}

      {!offersQuery.isLoading && !offersQuery.isError && sorted.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('offers.empty')}
        </Typography>
      )}

      <Stack spacing={2}>
        {sorted.map((offer) => {
          const sendBlocked = offer.status === 'draft' && pendingExists;
          return (
            <Box
              key={offer.id}
              data-testid={`work-order-offer-${offer.id}`}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2">
                    {t('offers.offerTitle', { n: offer.sequenceNo })}
                  </Typography>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[offer.status]}
                    label={t(`offers.status.${offer.status}`)}
                  />
                  {offer.title ? (
                    <Typography variant="body2" color="text.secondary">
                      {offer.title}
                    </Typography>
                  ) : null}
                </Stack>
                <Typography variant="body2" fontWeight={600}>
                  {formatCentsForDisplay(offer.proposedTotalGrossCents ?? 0)}
                </Typography>
              </Stack>

              <Stack spacing={0.75} sx={{ mb: 1 }}>
                {offer.lines.map((line) => (
                  <Stack
                    key={line.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="body2"
                      {...(line.decision === 'declined' ? { 'data-testid': 'offer-line-declined' } : {})}
                      sx={{
                        textDecoration: line.decision === 'declined' ? 'line-through' : 'none',
                      }}
                    >
                      {line.description}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">
                        {formatCentsForDisplay(lineGrossCents(line))}
                      </Typography>
                      <Chip
                        size="small"
                        color={LINE_DECISION_COLOR[line.decision] ?? 'default'}
                        label={t(`offers.lineDecision.${line.decision}`)}
                      />
                    </Stack>
                  </Stack>
                ))}
              </Stack>

              <Stack spacing={0.25} sx={{ mb: canUpdate ? 1 : 0 }}>
                {offer.sentAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('offers.sentAt', { date: formatDate(offer.sentAt) })}
                  </Typography>
                )}
                {offer.latestSend && (
                  <Typography variant="caption" color="text.secondary">
                    {t('offers.latestSend', {
                      channel:
                        isOfferDeliverChannel(offer.latestSend.sentVia)
                          ? t(`offers.deliverChannel.${offer.latestSend.sentVia}`)
                          : offer.latestSend.sentVia,
                      recipient: offer.latestSend.recipient,
                      date: formatDate(offer.latestSend.sentAt),
                    })}
                  </Typography>
                )}
                {offer.expiresAt && (
                  <Typography variant="caption" color="text.secondary">
                    {t('offers.expiresAt', { date: formatDate(offer.expiresAt) })}
                  </Typography>
                )}
                {offer.latestSend?.sentVia === 'email' && (
                  <Box sx={{ pt: 1 }}>
                    <EmailDeliveryHistory
                      entityType="work_order_offer"
                      entityId={offer.id}
                      title={tc('emailDelivery.offerTitle')}
                    />
                  </Box>
                )}
              </Stack>

              {canUpdate && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {offer.status === 'draft' && (
                    <Tooltip title={sendBlocked ? t('offers.sendDisabledPending') : ''}>
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={sendBlocked}
                          onClick={() => {
                            setSendIsResend(false);
                            setSendTarget(offer);
                          }}
                        >
                          {t('offers.send')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {isPendingOfferStatus(offer.status) && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setDecisionTarget(offer);
                        }}
                      >
                        {t('offers.recordAnswer')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSendIsResend(true);
                          setSendTarget(offer);
                        }}
                      >
                        {t('offers.resend')}
                      </Button>
                    </>
                  )}
                  {(offer.status === 'draft' || isPendingOfferStatus(offer.status)) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setCancelTarget(offer);
                      }}
                    >
                      {tc('cancel')}
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>

      {createOpen && (
        <CreateOfferDialog
          open={createOpen}
          workOrderId={workOrder.id}
          items={createItems}
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      )}
      {sendTarget && (
        <SendOfferDialog
          open
          workOrderId={workOrder.id}
          offer={sendTarget}
          contactEmail={workOrder.contactEmail}
          contactPhone={workOrder.contactPhone}
          resend={sendIsResend}
          onClose={() => {
            setSendTarget(null);
          }}
        />
      )}
      {decisionTarget && (
        <RecordOfferDecisionDialog
          open
          workOrderId={workOrder.id}
          offer={decisionTarget}
          onClose={() => {
            setDecisionTarget(null);
          }}
        />
      )}
      {cancelTarget && (
        <CancelOfferDialog
          open
          workOrderId={workOrder.id}
          offer={cancelTarget}
          onClose={() => {
            setCancelTarget(null);
          }}
        />
      )}
    </Box>
  );
}
